import axios from "axios";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getHeader } from "../../../service/auth";
import Paper from "@mui/material/Paper";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { motion } from 'framer-motion';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import LoadingState from "../../../components/LoadingState";
import { StyledTableRow } from "../../../utils/styledTable";
import { useNavigate } from "react-router-dom";
import {
  DatasetLinked,
  DeleteForever,
  Edit,
  List,
} from "@mui/icons-material";
import SnackAlert from "../../../components/SnackAlert";
import themeNutriFast from "../../../styles/NutriFastColors";
import BreadcrumbNav from "../../../components/BreadcrumbNav";
import useRetryableApi from "../../../hooks/useRetryableApi";
import PageTransition from "../../../components/PageTransition";


const BtnAnnuaire = {
  /* color: "grey",*/
  display: "contents",
  float: "left",
};

const padTr = {
  padding: "6px",
};

const pAnuaireBtn = {
  color: "grey",
  display: "contents",
  float: "left",
};

const GestionUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = getHeader();
  let navigate = useNavigate();
  const { executeWithRetry, getErrorMessage, isRetrying } = useRetryableApi();

  const [alert, setAlert] = useState({
    message: "",
    severity: "info",
    open: false,
  });

  // États pour la pop-up de confirmation de suppression
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setAlert({ message: "", severity: "info", open: false });
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      
      await executeWithRetry(
        () => axios.get(process.env.REACT_APP_ADMIN, { headers }),
        (response) => {
          setUsers(response.data);
          setLoading(false);
        },
        (error, attemptCount) => {
          setAlert({
            message: getErrorMessage(error, attemptCount),
            severity: "error",
            open: true,
          });
          setLoading(false);
        }
      );
    };

    fetchUsers();
  }, []); // Tableau vide pour exécution unique au montage

  const handleEdit = useCallback((index) => {
    navigate("/admin/user/edit", { state: users[index] });
  }, [navigate, users]);

  const handleShow = useCallback((index) => {
    navigate("/admin/user/show", { state: users[index] });
  }, [navigate, users]);

  const handleDelete = useCallback((index) => {
    setUserToDelete(index);
    setConfirmDialogOpen(true);
  }, []);

  const handleDeleteUser = async () => {
    if (userToDelete !== null) {
      let user = users[userToDelete];
      setDeletingUser(true);

      await executeWithRetry(
        () => axios.delete(process.env.REACT_APP_ADMIN + "/user/" + user.id, { headers }),
        () => {
          setAlert({
            message: "Utilisateur supprimé",
            severity: "success", 
            open: true,
          });
          users.splice(userToDelete, 1);
          setUsers([...users]);
          setDeletingUser(false);
          setConfirmDialogOpen(false);
          setUserToDelete(null);
        },
        (error, attemptCount) => {
          setAlert({
            message: getErrorMessage(error, attemptCount),
            severity: "error",
            open: true,
          });
          setDeletingUser(false);
          setConfirmDialogOpen(false);
          setUserToDelete(null);
        }
      );
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setUserToDelete(null);
  };

  const handleAssoc = useCallback((index) => {
    navigate("/admin/assoc_quest", { state: users[index] });
  }, [navigate, users]);

  const isEmpty = useMemo(() => users.length === 0, [users]);

  const breadcrumbItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Gestion des utilisateurs', path: '/admin/gestion_user' }
  ];

  return (
    <PageTransition>
      <Paper style={{ padding: 30 }}>
        <BreadcrumbNav items={breadcrumbItems} />
      
      {loading && (
        <LoadingState 
          message={isRetrying ? "Nouvelle tentative de connexion..." : "Chargement des utilisateurs..."} 
        />
      )}

      {!loading && isEmpty && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Aucun utilisateur trouvé</p>
        </div>
      )}

      {!loading && users.length > 0 && (
      <TableContainer component={Paper}>
          <Table sx={{ minWidth: { xs: 320, md: 650 } }} aria-label="Liste des utilisateurs">
            <TableHead>
              <TableRow>
                <TableCell style={padTr}>
                  <p style={BtnAnnuaire}>ID</p>
                </TableCell>
                <TableCell style={padTr}>
                  <p style={{display:"contents"}}>Username</p>
                </TableCell>
                <TableCell style={padTr}>
                  <p style={BtnAnnuaire}>Code</p>
                </TableCell>
                <TableCell style={padTr}>
                  <p style={BtnAnnuaire}>Date de naissance</p>
                </TableCell>
                <TableCell style={padTr}>
                  <p style={BtnAnnuaire}>Actions</p>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((row, index) => (
                
                <StyledTableRow
                  key={`user-${row.id}`}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.birth_date}</TableCell>
                  <TableCell>
                    <Button 
                      style={pAnuaireBtn }
                      title="Editer ce volontaire"
                      aria-label={`Editer l'utilisateur ${row.name}`}
                      onClick={() => handleEdit(index)}
                    >
                      <Edit  style={{color:themeNutriFast.palette.primary.dark}} className="tablecelladd"/>
                    </Button>
                    <Button
                      style={pAnuaireBtn}
                      title="Voir le(s) questionnaire(s)"
                      aria-label={`Voir les questionnaires de ${row.name}`}
                      onClick={() => handleShow(index)}
                    >
                      <List  className="tablecelladd" style={{color:themeNutriFast.palette.primary.dark}}/>
                    </Button>
                    <Button
                      style={pAnuaireBtn}
                      title="Associer un questionnaire"
                      aria-label={`Associer un questionnaire à ${row.name}`}
                      onClick={() => handleAssoc(index)}
                    >
                      <DatasetLinked className="tablecelladd" style={{color:themeNutriFast.palette.primary.dark}}/>
                    </Button>                    
                    <Button
                      style={pAnuaireBtn}
                      title="Supprimer ce volontaire"
                      aria-label={`Supprimer l'utilisateur ${row.name}`}
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteForever className="tablecelladd" style={{color:themeNutriFast.palette.primary.dark}}/>
                    </Button>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
      </TableContainer>
      )}



   
       
       

    
        
   
<div className="fab-container">
    <span>Ajouter un volontaire</span> 
    <div style={{ backgroundColor: themeNutriFast.palette.primary.dark }}
      className="button-add iconbutton" 
      title="Ajouter un volontaire"
      role="button"
      tabIndex={0}
      aria-label="Ajouter un nouveau volontaire"
      onClick={() => {
          navigate("/admin/user/add");
        }}
      onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            navigate("/admin/user/add");
          }
        }}>
      <span>
        <AddOutlinedIcon>
         </AddOutlinedIcon>
       </span> 
  </div>
</div>
    
      {/* Dialog de confirmation de suppression */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmation de suppression</DialogTitle>
        <DialogContent>
          <p>Êtes-vous sûr de vouloir supprimer cet utilisateur ?</p>
          {userToDelete !== null && (
            <p><strong>{users[userToDelete]?.name}</strong></p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteUser}
            disabled={deletingUser}
            style={{
              backgroundColor: themeNutriFast.palette.primary.dark,
              color: 'white'
            }}
            startIcon={deletingUser ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deletingUser ? 'Suppression...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        handleClose={handleClose}
      />
      </Paper>
    </PageTransition>
  );
};

export default GestionUsers;
